/**
 * Header Navigation V2 - Interaction Handling
 * Handles desktop dropdowns and mobile drawer menu
 */

class HeaderNavigationV2 {
  constructor(headerElement) {
    this.header = headerElement;
    if (!this.header) {
      return;
    }

    this.closeTimer = null; // Timer for delayed dropdown closing

    this.initDesktopDropdowns();
    this.initMobileMenu();
    this.initCartUpdate();
    this.setHeaderHeight();
  }

  /**
   * Initialize desktop dropdown menus
   * Handles hover behavior and click-outside-to-close
   */
  initDesktopDropdowns() {
    const dropdowns = this.header.querySelectorAll('.header-navigation-v2__dropdown');
    
    dropdowns.forEach((dropdown, index) => {
      const summary = dropdown.querySelector('summary');
      const content = dropdown.querySelector('.header-navigation-v2__dropdown-content');
      
      // Desktop: Open on hover with delayed close
      if (window.innerWidth >= 768) {
        dropdown.addEventListener('mouseenter', () => {
          // Clear any pending close timer
          if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
          }
          
          this.closeAllDropdowns();
          dropdown.setAttribute('open', '');
        });

        dropdown.addEventListener('mouseleave', () => {
          // Delay closing to allow mouse movement to dropdown content
          this.closeTimer = setTimeout(() => {
            dropdown.removeAttribute('open');
            this.closeTimer = null;
          }, 300); // 300ms delay
        });
      }

      // Mobile/tablet: Toggle on click
      summary.addEventListener('click', (e) => {
        if (window.innerWidth < 768) {
          e.preventDefault();
          
          if (dropdown.hasAttribute('open')) {
            dropdown.removeAttribute('open');
          } else {
            this.closeAllDropdowns();
            dropdown.setAttribute('open', '');
          }
        }
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header-navigation-v2__dropdown')) {
        this.closeAllDropdowns();
      }
    });

    // Close dropdowns on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * Initialize mobile menu drawer
   */
  initMobileMenu() {
    const toggle = this.header.querySelector('.header-navigation-v2__mobile-toggle');
    const drawer = this.header.querySelector('.header-navigation-v2__mobile-drawer');
    
    if (!toggle || !drawer) return;

    let lastTouchTime = 0;
    const toggleMenu = () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      
      if (isOpen) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    };

    toggle.addEventListener('touchend', (event) => {
      if (window.innerWidth >= 768) return;
      lastTouchTime = Date.now();
      event.preventDefault();
      toggleMenu();
    }, { passive: false });

    toggle.addEventListener('click', (event) => {
      if (window.innerWidth >= 768) return;
      if (Date.now() - lastTouchTime < 500) {
        event.preventDefault();
        return;
      }
      toggleMenu();
    });

    toggle.addEventListener('keydown', (event) => {
      const key = event.code.toUpperCase();
      if (key !== 'SPACE' && key !== 'ENTER') return;
      event.preventDefault();
      toggleMenu();
    });

    // Close mobile menu when clicking on a link
    const links = drawer.querySelectorAll('a');
    links.forEach((link) => {
      link.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMobileMenu();
      }
    });
  }

  /**
   * Open mobile menu drawer
   */
  openMobileMenu() {
    const toggle = this.header.querySelector('.header-navigation-v2__mobile-toggle');
    const drawer = this.header.querySelector('.header-navigation-v2__mobile-drawer');
    
    toggle.setAttribute('aria-expanded', 'true');
    drawer.classList.add('is-open');
    
    // Store scroll position and lock body scroll (iOS Safari compatible)
    this.scrollPosition = window.scrollY;
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add('mobile-menu-open');
  }

  /**
   * Close mobile menu drawer
   */
  closeMobileMenu() {
    const toggle = this.header.querySelector('.header-navigation-v2__mobile-toggle');
    const drawer = this.header.querySelector('.header-navigation-v2__mobile-drawer');
    
    toggle.setAttribute('aria-expanded', 'false');
    drawer.classList.remove('is-open');
    
    // Restore scroll position and unlock body scroll
    document.body.classList.remove('mobile-menu-open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition || 0);
  }

  /**
   * Close all desktop dropdown menus
   */
  closeAllDropdowns() {
    const dropdowns = this.header.querySelectorAll('.header-navigation-v2__dropdown[open]');
    dropdowns.forEach((dropdown) => {
      dropdown.removeAttribute('open');
    });
  }

  /**
   * Set CSS variable for header height (used for positioning dropdowns)
   */
  setHeaderHeight() {
    const updateHeight = () => {
      // Measure just the nav bar (not the whole header container with dropdowns)
      const navBar = document.querySelector('.header-navigation-v2__nav');
      if (!navBar) {
        return;
      }
      
      // Only update if the element is visible
      const rect = navBar.getBoundingClientRect();
      if (rect.height === 0) {
        // Element not visible yet, try again soon
        setTimeout(updateHeight, 100);
        return;
      }
      
      // Use rect.bottom directly since dropdown uses position:fixed (viewport-relative)
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${rect.bottom}px`);
    };

    // Initial update with small delay to ensure rendering is complete
    setTimeout(updateHeight, 50);
    
    // Update on resize and scroll
    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);
  }

  /**
   * Initialize cart count update listener
   * Updates cart count when cart changes
   */
  initCartUpdate() {
    const cartCounts = this.header.querySelectorAll('[data-cart-count]');
    
    // Listen for cart updates (Shopify theme event)
    document.addEventListener('cart:updated', (e) => {
      const itemCount = e.detail?.cart?.item_count || 0;
      cartCounts.forEach((count) => {
        count.textContent = itemCount;
      });
    });

    // Also listen for custom cart update events
    window.addEventListener('cart-updated', () => {
      this.fetchCartCount();
    });
  }

  /**
   * Fetch current cart count from Shopify API
   */
  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCounts = this.header.querySelectorAll('[data-cart-count]');
      
      cartCounts.forEach((count) => {
        count.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error fetching cart count:', error);
    }
  }
}

/**
 * Initialize all header navigation instances
 * Handles multiple headers for A/B testing scenarios
 */
function initializeAllHeaders() {
  const headers = document.querySelectorAll('header.header-navigation-v2');
  headers.forEach((header) => {
    if (!header.dataset.initialized) {
      new HeaderNavigationV2(header);
      header.dataset.initialized = 'true';
    }
  });
}

/**
 * Wait for Intelligems/Convert to add the header-navigation-v2 class to body
 * Then initialize the header navigation
 */
function initializeWhenReady() {
  // Initialize now and keep watching for delayed mounts/re-renders.
  const runInit = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initializeAllHeaders();
      });
    });
  };

  runInit();

  const observer = new MutationObserver(() => {
    initializeAllHeaders();
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true
  });

  setTimeout(runInit, 1500);
  setTimeout(runInit, 4000);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWhenReady);
} else {
  initializeWhenReady();
}

// Re-initialize on Shopify section re-render
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('header.header-navigation-v2')) {
    initializeAllHeaders();
  }
});
